import FrequentlyAskedQuestions from '@app-studio-web/ui/components/FrequentlyAskedQuestions'
import LimitedTimeOfferBanner from '@app-studio-web/ui/components/LimitedTimeOfferBanner'
import LimitedTimeOfferDialog from '@app-studio-web/ui/components/LimitedTimeOfferDialog'
import MoneyBackGuarantee from '@app-studio-web/ui/components/MoneyBackGuarantee'
import Plans from '@app-studio-web/ui/components/Plans'
import type { GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import ErrorPage from '@app-studio-web/ui/pages/ErrorPage'
import LoadingPage from '@app-studio-web/ui/pages/LoadingPage'
import type { Plan, StripePrice } from '@app-studio-web/ui/types'
import { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Await, useLoaderData } from 'react-router-dom'

// Bumi's checkout shows the shared three-plan "-vo" pricing (1 / 3 / 12 months,
// with the 3-month plan recommended in the middle).
const SUBSCRIPTION_PLAN_ORDER: Array<'first' | 'top' | 'second'> = [
  'first',
  'top',
  'second',
]

function introPriceIdForPlan(
  plan: Plan,
  prices: GetPricesData,
): string | undefined {
  switch (plan) {
    case 'top':
      return prices.topIntroPrice?.id ?? prices.topPrice?.id
    case 'first':
      return prices.firstIntroPrice?.id ?? prices.firstPrice?.id
    case 'second':
      return prices.secondIntroPrice?.id ?? prices.secondPrice?.id
    case 'one-time':
      return prices.oneTimePrice?.id
    default: {
      const exhaustive: never = plan
      return exhaustive
    }
  }
}

function fullPriceForPlan(
  plan: Plan,
  prices: GetPricesData,
): StripePrice | undefined {
  switch (plan) {
    case 'top':
      return prices.topPrice
    case 'first':
      return prices.firstPrice
    case 'second':
      return prices.secondPrice
    case 'one-time':
      return prices.oneTimePrice
    default: {
      const exhaustive: never = plan
      return exhaustive
    }
  }
}

function introPriceForPlan(
  plan: Plan,
  prices: GetPricesData,
): StripePrice | undefined {
  switch (plan) {
    case 'top':
      return prices.topIntroPrice
    case 'first':
      return prices.firstIntroPrice
    case 'second':
      return prices.secondIntroPrice
    case 'one-time':
      return undefined
    default: {
      const exhaustive: never = plan
      return exhaustive
    }
  }
}

export default function PlansPage() {
  const { t } = useTranslation()
  const [countdownUntil, setCountdownUntil] = useState(0)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>('top')

  useEffect(() => {
    const dialogTimeout = setTimeout(() => setIsDialogOpen(true), 30000)
    setCountdownUntil(Date.now() + 9 * 60 * 1000)

    return () => clearTimeout(dialogTimeout)
  }, [])

  const data = useLoaderData() as GetPricesData

  if (isSubmitting) return <LoadingPage />

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data}>
        {(getPricesData: GetPricesData) => (
          <div className="bg-app-bg min-h-screen">
            <div className="bg-app-bg/95 sticky top-0 z-30 backdrop-blur-sm">
              <LimitedTimeOfferBanner
                countdownUntil={countdownUntil}
                currency={getPricesData.currency}
                isSubmitting={isSubmitting}
                plan={selectedPlan}
                priceId={introPriceIdForPlan(selectedPlan, getPricesData)}
                setIsSubmitting={setIsSubmitting}
                stripePriceFull={fullPriceForPlan(selectedPlan, getPricesData)}
              />
            </div>

            <main className="mx-auto max-w-[480px] px-6 pt-6 pb-14 md:max-w-[600px] md:px-10">
              <h1 className="text-primary mb-8 text-center font-serif text-3xl leading-tight font-normal">
                {t('pages.checkout.plans.heading')}
              </h1>

              <section className="mb-12">
                <Plans
                  countdownUntil={countdownUntil}
                  currency={getPricesData.currency}
                  firstIntroPrice={getPricesData.firstIntroPrice}
                  firstPrice={getPricesData.firstPrice}
                  isSubmitting={isSubmitting}
                  onPlanChange={setSelectedPlan}
                  secondIntroPrice={getPricesData.secondIntroPrice}
                  secondPrice={getPricesData.secondPrice}
                  selectedPlan={selectedPlan}
                  setIsSubmitting={setIsSubmitting}
                  subscriptionPlanOrder={SUBSCRIPTION_PLAN_ORDER}
                  topIntroPrice={getPricesData.topIntroPrice}
                  topPrice={getPricesData.topPrice}
                />
              </section>

              <section className="mb-12">
                <MoneyBackGuarantee />
              </section>

              <section className="mb-12 text-left">
                <FrequentlyAskedQuestions />
              </section>

              <section className="mb-6">
                <Plans
                  countdownUntil={countdownUntil}
                  currency={getPricesData.currency}
                  firstIntroPrice={getPricesData.firstIntroPrice}
                  firstPrice={getPricesData.firstPrice}
                  isSubmitting={isSubmitting}
                  onPlanChange={setSelectedPlan}
                  secondIntroPrice={getPricesData.secondIntroPrice}
                  secondPrice={getPricesData.secondPrice}
                  selectedPlan={selectedPlan}
                  setIsSubmitting={setIsSubmitting}
                  subscriptionPlanOrder={SUBSCRIPTION_PLAN_ORDER}
                  topIntroPrice={getPricesData.topIntroPrice}
                  topPrice={getPricesData.topPrice}
                />
              </section>
            </main>

            <LimitedTimeOfferDialog
              currency={getPricesData.currency}
              isSubmitting={isSubmitting}
              onClose={() => setIsDialogOpen(false)}
              open={isDialogOpen}
              plan={selectedPlan}
              setIsSubmitting={setIsSubmitting}
              stripePriceFull={fullPriceForPlan(selectedPlan, getPricesData)}
              stripePriceIntro={introPriceForPlan(selectedPlan, getPricesData)}
            />
          </div>
        )}
      </Await>
    </Suspense>
  )
}
